import { TApplication } from '@/interfaces'
import { TFormFile } from '@/interfaces/form'
import { ApplicationsState, FormState } from '@/state'
import { applicationsStateActions, formStateActions } from '@/state/actions'
import { useSignal } from '@preact/signals-react'
import { Button, notification, Row, Tooltip, Upload } from 'antd'
import { RcFile } from 'antd/es/upload'
import React from 'react'
import { FaEye } from 'react-icons/fa6'
import { GrAttachment } from 'react-icons/gr'
import { RiFindReplaceLine } from 'react-icons/ri'

const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']

export default function SubsectionFileActions({
  fileInfo,
}: {
  fileInfo: TFormFile
}) {
  const fileAttached = useSignal<string | ArrayBuffer | null>(null)
  const recentAttached = useSignal<boolean>(false)

  React.useEffect(() => {
    const currentEntityName = FormState.currentEntity.value?.name
    const currentApplicationValues = ApplicationsState.currentApplication.value
    const currentFileFieldName = fileInfo?.name

    if (currentEntityName && currentApplicationValues && currentFileFieldName) {
      const currentEntityValues = currentApplicationValues[
        currentEntityName
      ] as TApplication

      if (currentEntityValues) {
        const currentFileValue =
          currentEntityValues[currentFileFieldName] ?? null

        ApplicationsState.currentEntityFilesData.value = {
          ...ApplicationsState.currentEntityFilesData.peek(),
          [currentFileFieldName]: currentFileValue as string,
        }

        fileAttached.value = currentFileValue as string
      }
    }
  }, [])

  function beforeUpload(file: RcFile) {
    const isGt2M = file.size / 1024 / 1024 > 2

    if (isGt2M) {
      return notification.warning({
        message: 'Tamaño no permitido',
        description:
          'El archivo que intentas subir supera el límite permitido de 2 MB, inténtalo nuevamente',
        placement: 'top',
      })
    }

    if (!allowedTypes.includes(file.type)) {
      notification.warning({
        message: 'Tipo de archivo no permitido',
        description: 'Solo se permiten archivos de tipo PNG, JPEG, JPG o PDF.',
        placement: 'top',
      })
      return false
    }

    const reader = new FileReader()

    reader.onload = () => {
      const fileData = reader.result as string

      if (fileData && fileInfo.name) {
        fileAttached.value = fileData
        recentAttached.value = true

        applicationsStateActions.setCurrentEntityFile(fileData, fileInfo.name)

        setTimeout(() => {
          recentAttached.value = false
        }, 5000)

        notification.success({
          message: fileAttached.peek()
            ? 'Archivo reemplazado correctamente'
            : 'Archivo adjunto correctamente',
          placement: 'top',
        })
      }
    }

    reader.readAsDataURL(file)

    return false
  }

  function handleViewFile() {
    formStateActions.viewFile(fileAttached.peek() as string)
  }

  const canChangeFile = ApplicationsState.viewMode.value.edit

  return (
    <Row justify='space-around' gutter={10} className='file-actions'>
      {canChangeFile && (
        <Tooltip
          placement='left'
          title={fileAttached.value ? 'Reemplazar' : 'Adjuntar'}
        >
          <Upload
            accept={allowedTypes.join(', ')}
            beforeUpload={beforeUpload}
            showUploadList={false}
          >
            <Button
              className={recentAttached.value ? 'recent' : ''}
              shape='circle'
              icon={
                fileAttached.value ? <RiFindReplaceLine /> : <GrAttachment />
              }
            />
          </Upload>
        </Tooltip>
      )}
      {fileAttached.value && (
        <Tooltip placement='left' title='Ver archivo'>
          <Button
            disabled={false}
            shape='circle'
            icon={<FaEye />}
            onClick={handleViewFile}
          />
        </Tooltip>
      )}
    </Row>
  )
}
